import { create } from 'zustand'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Default settings values for Terrarium
const DEFAULT_SETTINGS = {
  // Plant settings
  defaultPlantType: 'flower',
  defaultInsectType: 'ladybug',
  maxPlants: 50,
  maxInsects: 30,

  // Growth settings
  growthRate: 1,              // Growth % per update cycle
  healthDecayRate: 0.5,       // Health % decay per minute when not watered (0.5 = ~3.3 hours to die)
  waterDurationMinutes: 30,   // How long water lasts
  pauseDecayWhenOffline: true, // Pause health decay when stream is offline
  growthUpdateInterval: 60000, // How often to fetch growth updates (ms)

  // Animation durations (ms)
  waterAnimationDuration: 2000,
  harvestAnimationDuration: 1500,
  spawnDuration: 800,
  despawnDuration: 500,

  // Visual settings
  plantScale: 1.2,            // Multiplier for plant size
  showGrowthBars: true,
  showUsernames: true,

  // Feature toggles
  allowWatering: true,        // Allow viewers to water plants
  allowPollinating: true,     // Allow insects to pollinate plants
  allowVisiting: true,        // Allow cross-entity visits
  allowBloom: true,           // Allow bloom command

  // Join gating
  requireSubscription: false,
  requiredTier: 1000,         // Tier 1 = 1000, Tier 2 = 2000, Tier 3 = 3000

  // Insect roaming
  roamInterval: 8000,         // ms between target picks
  updateInterval: 200,        // ms between position updates
  roamSpeed: 0.3,             // % per update
  minX: 5,
  maxX: 95,
  insectMinY: 25,
  insectMaxY: 55,

  // Display toggles
  showGround: true,            // Ground/soil layer
  showGlassFrame: true,        // Glass rim and shine overlay
  showWaterParticles: true,    // Water drop particles on plants
  showNotifications: true,     // Event notification popups
  showChatBubbles: true,       // Chat bubbles above entities

  // Admin settings
  modCanUseAdminCommands: true,
  broadcasterOnlyAdmin: false,

  // Bits / Monetization
  enableBitsFeatures: true,
  showBitsNotifications: true,
  showBitsLeaderboard: false
}

const useSettingsStore = create((set, get) => ({
  settings: { ...DEFAULT_SETTINGS },
  broadcasterLogin: null,
  isLoading: false,
  hasChanges: false,
  isPanelOpen: false,

  // Set broadcaster context
  setBroadcasterLogin: (login) => set({ broadcasterLogin: login }),

  // Toggle settings panel
  togglePanel: () => set(state => ({ isPanelOpen: !state.isPanelOpen })),
  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),

  // Update a single setting
  updateSetting: (key, value) => {
    set(state => ({
      settings: { ...state.settings, [key]: value },
      hasChanges: true
    }))
  },

  // Update multiple settings at once
  updateSettings: (newSettings) => {
    set(state => ({
      settings: { ...state.settings, ...newSettings },
      hasChanges: true
    }))
  },

  // Reset to defaults
  resetToDefaults: () => {
    set({ settings: { ...DEFAULT_SETTINGS }, hasChanges: true })
  },

  // Load settings from API
  loadFromApi: async (broadcasterLogin) => {
    set({ isLoading: true, broadcasterLogin })
    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/settings`)
      if (response.ok) {
        const data = await response.json()
        if (data.settings) {
          set({
            settings: { ...DEFAULT_SETTINGS, ...data.settings },
            hasChanges: false
          })
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  // Save settings to API
  saveToApi: async () => {
    const { broadcasterLogin, settings } = get()
    if (!broadcasterLogin) return false

    set({ isLoading: true })
    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${broadcasterLogin}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings })
      })
      if (response.ok) {
        set({ hasChanges: false })
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to save settings:', error)
      return false
    } finally {
      set({ isLoading: false })
    }
  },

  // Get a setting value (with default fallback)
  getSetting: (key) => {
    const { settings } = get()
    return settings[key] ?? DEFAULT_SETTINGS[key]
  }
}))

export { DEFAULT_SETTINGS }
export default useSettingsStore